import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Radar } from "react-chartjs-2";
import ModuleContainer from "./ModuleContainer";
import { colors } from "../../lib/designTokens";
import { getRadarSeriesColor, RADAR_COMPETITOR_COLORS } from "../../lib/radarPalette";
import React from "react";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

type RadarStatus = "loading" | "ready" | "empty" | "error" | "unavailable";

type RadarRow = {
  category_group: string;
  category_label: string;
  stage_order: number;
  brand: string;
  mentions: number;
};

const RADAR_STAGES = [
  {
    key: "category_discovery",
    label: "Category Discovery",
    definition:
      "How often your brand appears when AI answers broad category or solution-discovery prompts."
  },
  {
    key: "alternatives_replacements",
    label: "Alternatives",
    definition:
      "How often your brand appears when buyers ask for alternatives, substitutes, or replacement options."
  },
  {
    key: "comparisons",
    label: "Comparisons",
    definition:
      "How often your brand appears when AI compares brands, products, or providers during evaluation."
  },
  {
    key: "use_case_fit",
    label: "Use-Case Fit",
    definition:
      "How well AI connects your brand to specific buyer needs, projects, or use cases."
  },
  {
    key: "pain_point_led",
    label: "Pain Points",
    definition:
      "How often AI recommends or mentions your brand when buyers describe a problem instead of a product."
  },
  {
    key: "purchase_shortlist",
    label: "Purchase Shortlist",
    definition:
      "How often your brand appears close to purchase decisions, shortlists, or recommendation prompts."
  }
];

const AXIS_DEFINITIONS: Record<string, string> = RADAR_STAGES.reduce(
  (acc, stage) => {
    acc[stage.label.toLowerCase()] = stage.definition;
    return acc;
  },
  {} as Record<string, string>
);

function normalizeBrandName(str?: string) {
  return (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function safeIndex(value: number, max: number, hasData: boolean) {
  if (!hasData) return 0;
  if (!max || max === 0) return 0;
  return Math.round((value / max) * 85 + 15);
}

function getBrandDisplayName(brand: string) {
  return brand
    .split(" ")
    .map((part) =>
      part.length ? part.charAt(0).toUpperCase() + part.slice(1) : part
    )
    .join(" ");
}

export default function AIRadar({
  account,
  mode = "demo",
  customerBrand
}: {
  account: string;
  mode?: "competitive" | "demo";
  customerBrand?: string;
}) {
  const isDemoMode = mode === "demo";
  const [chartData, setChartData] = useState<any>(null);
  const [status, setStatus] = useState<RadarStatus>("loading");
  const [statusMessage, setStatusMessage] = useState<string>("");

  useEffect(() => {
    if (!account) {
      setStatus("unavailable");
      setStatusMessage("This module is not available for this account yet.");
      return;
    }

    fetchRadar();
  }, [account, mode, customerBrand]);

  async function fetchRadar() {
    setChartData(null);
    setStatus("loading");
    setStatusMessage("");

    const { data: runData, error: runError } = await supabase
      .from("runs")
      .select("run_id")
      .eq("account_id", account)
      .eq("status", "completed")
      .order("completed_at", { ascending: false, nullsFirst: false })
      .limit(2);

    if (runError) {
      console.error("[AIRadar] Failed to load latest run", runError);
      setStatus("error");
      setStatusMessage("This module could not load right now.");
      return;
    }

    if (!runData?.length) {
      setStatus("empty");
      setStatusMessage("Not enough data available yet.");
      return;
    }

    const recentRunIds = runData.map((run: any) => run.run_id);

    const { data, error } = await supabase
      .from("toren_competitive_radar_role_v1")
      .select("category_group, category_label, stage_order, brand, mentions")
      .eq("account_id", account)
      .in("run_id", recentRunIds);

    if (error) {
      console.error("[AIRadar] Failed to load radar data", error);
      setStatus("error");
      setStatusMessage("This module could not load right now.");
      return;
    }

    if (!data?.length) {
      setStatus("empty");
      setStatusMessage("Not enough data available yet.");
      return;
    }

    const categoryTotals: Record<string, number> = {};
    const grouped: Record<string, any> = {};

    RADAR_STAGES.forEach((stage) => {
      categoryTotals[stage.key] = 0;
    });

    (data as RadarRow[]).forEach((row) => {
      const category = row.category_group;
      const brand = row.brand?.trim();

      if (!category || !brand || !(category in categoryTotals)) {
        return;
      }

      const mentions = Number(row.mentions || 0);
      categoryTotals[category] += mentions;

      if (!grouped[brand]) {
        grouped[brand] = {
          brand,
          total: 0
        };

        RADAR_STAGES.forEach((stage) => {
          grouped[brand][stage.key] = 0;
        });
      }

      grouped[brand][category] += mentions;
      grouped[brand].total += mentions;
    });

    const entries = Object.values(grouped);

    if (!entries.length) {
      setStatus("empty");
      setStatusMessage("Not enough data available yet.");
      return;
    }

    const summaryData = entries
      .map((row: any) => {
        const output: any = {
          brand: row.brand,
          displayBrand: getBrandDisplayName(row.brand),
          total: row.total
        };

        RADAR_STAGES.forEach((stage) => {
          output[`${stage.key}_score`] = categoryTotals[stage.key]
            ? (row[stage.key] / categoryTotals[stage.key]) * 100
            : 0;
        });

        return output;
      })
      .sort((a: any, b: any) => b.total - a.total);

    const hasAnyPresence = summaryData.some((b: any) => b.total > 0);
    const primaryBrand = customerBrand?.trim() || "";

    if (!primaryBrand) {
      setStatus("empty");
      setStatusMessage("Brand information is not available for this account.");
      return;
    }

    let finalPrimary =
      summaryData.find(
        (b: any) =>
          normalizeBrandName(b.brand) === normalizeBrandName(primaryBrand) ||
          normalizeBrandName(b.displayBrand) === normalizeBrandName(primaryBrand)
      ) || null;

    if (!finalPrimary) {
      finalPrimary = {
        brand: primaryBrand,
        displayBrand: primaryBrand,
        total: 0
      };

      RADAR_STAGES.forEach((stage) => {
        finalPrimary[`${stage.key}_score`] = 0;
      });
    }

    const hasPrimaryData = (finalPrimary?.total || 0) > 0;

    const competitors = summaryData
      .filter(
        (b: any) =>
          normalizeBrandName(b.brand) !== normalizeBrandName(primaryBrand) &&
          normalizeBrandName(b.displayBrand) !== normalizeBrandName(primaryBrand)
      )
      .slice(0, 4);

    const finalData = [finalPrimary, ...competitors];

    const maxPerCategory: Record<string, number> = {};

    RADAR_STAGES.forEach((stage) => {
      maxPerCategory[stage.key] = Math.max(
        ...summaryData.map((b: any) => b[`${stage.key}_score`] || 0),
        0
      );
    });

    const datasets = finalData.map((row: any, i: number) => {
      const isPrimary =
        normalizeBrandName(row.brand) === normalizeBrandName(primaryBrand) ||
        normalizeBrandName(row.displayBrand) === normalizeBrandName(primaryBrand);

      const color = isPrimary
        ? getRadarSeriesColor("customer")
        : isDemoMode
        ? "#9ca3af"
        : getRadarSeriesColor("competitor", (i - 1) % RADAR_COMPETITOR_COLORS.length);

      return {
        label: isPrimary ? `${primaryBrand} (You)` : row.displayBrand,
        isPrimary,
        rawScores: RADAR_STAGES.map((stage) =>
          Math.round(row[`${stage.key}_score`] || 0)
        ),
        data: RADAR_STAGES.map((stage) =>
          isPrimary && !hasPrimaryData
            ? 0
            : safeIndex(
                row[`${stage.key}_score`] || 0,
                maxPerCategory[stage.key],
                hasAnyPresence
              )
        ),
        fill: isPrimary,
backgroundColor: isPrimary
  ? "rgba(37, 99, 235, 0.16)"
  : isDemoMode
  ? "rgba(156, 163, 175, 0.06)"
  : `${color}18`,
borderColor: color,
borderWidth: isPrimary ? 4 : 1.5,
pointRadius: isPrimary ? 5 : isDemoMode ? 0 : 1.5,
pointHoverRadius: isPrimary ? 6 : 3
      };
    });

    setChartData({
      labels: RADAR_STAGES.map((stage) => stage.label),
      datasets
    });

    setStatus("ready");
  }

  function getRadarInsight() {
  if (!chartData?.datasets?.length) return "";

  const primary = chartData.datasets.find((d: any) => d.isPrimary);
  const competitors = chartData.datasets.filter((d: any) => !d.isPrimary);

  if (!primary || !primary.data) return "";

  const values = RADAR_STAGES.map((stage, index) => ({
    key: stage.key,
    label: stage.label,
    value: primary.data[index] || 0,
    rawScore: primary.rawScores?.[index] || 0
  }));

  const sorted = [...values].sort((a, b) => b.value - a.value);
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];

  const competitorAverages = RADAR_STAGES.map((stage, index) => {
    const competitorValues = competitors
      .map((dataset: any) => dataset.data?.[index] || 0)
      .filter((value: number) => Number.isFinite(value));

    const avg =
      competitorValues.length > 0
        ? competitorValues.reduce((sum: number, value: number) => sum + value, 0) /
          competitorValues.length
        : 0;

    return {
      key: stage.key,
      label: stage.label,
      avg
    };
  });

  const largestCompetitorPressure = [...competitorAverages].sort(
    (a, b) => b.avg - a.avg
  )[0];

  const hasAnyPrimaryPresence = values.some((stage) => stage.value > 0);

  if (!hasAnyPrimaryPresence) {
    return "Your brand was not consistently included across the latest organic competitive prompts. Competitor patterns show where AI visibility is forming across the buyer journey.";
  }

  if (weakest.key === "category_discovery") {
    return `${primary.label.replace(" (You)", "")} is strongest in ${
      strongest.label
    }, but competitors are more consistently included during early category discovery. This suggests an opportunity to improve category-level content that helps AI include the brand earlier in the buyer journey.`;
  }

  if (
    weakest.key === "alternatives_replacements" ||
    weakest.key === "comparisons"
  ) {
    return `${primary.label.replace(" (You)", "")} is strongest in ${
      strongest.label
    }, but its largest gap is ${
      weakest.label
    }. Strengthening comparison, alternative, and competitor-positioning content can help AI include the brand more often during evaluation-stage prompts.`;
  }

  if (weakest.key === "use_case_fit") {
    return `${primary.label.replace(" (You)", "")} is strongest in ${
      strongest.label
    }, but has a gap in Use-Case Fit. More project-specific and application-specific content can help AI connect the brand to concrete buyer needs.`;
  }

  if (weakest.key === "pain_point_led") {
    return `${primary.label.replace(" (You)", "")} is strongest in ${
      strongest.label
    }, but has a gap in Pain Points. Content that maps common buyer problems to the brand’s products can help AI recommend it when users describe a need rather than a category.`;
  }

  if (weakest.key === "purchase_shortlist") {
    return `${primary.label.replace(" (You)", "")} is strongest in ${
      strongest.label
    }, but is less consistently carried into Purchase Shortlist prompts. Buying guides, shortlist pages, and value-based recommendation content can help close this gap.`;
  }

  return `${primary.label.replace(" (You)", "")} is strongest in ${
    strongest.label
  }, while the largest competitor pressure appears in ${
    largestCompetitorPressure?.label || "the buyer journey"
  }. The clearest opportunity is to strengthen visibility in ${weakest.label}.`;
}

  if (status !== "ready" || !chartData) {
    return (
      <ModuleContainer
        title={isDemoMode ? "Buyer Journey Radar - Demo" : "Buyer Journey Radar"}
        state={status}
        message={statusMessage}
      />
    );
  }

  return (
    <ModuleContainer
      title={isDemoMode ? "AI Presence Radar - Demo" : "AI Presence Radar"}
    >
      <p style={{ marginBottom: 10, color: colors.textSecondary, fontSize: 14 }}>
        Shows where your brand is included across the AI-generated buyer journey, from early discovery through purchase-stage shortlists.
      </p>

      <div
        style={{
          width: "100%",
          maxWidth: 1100,
          height: 510,
          margin: "0 auto"
        }}
      >
        <Radar
          key={JSON.stringify(chartData.datasets.map((d: any) => d.label))}
          data={{
            labels: chartData.labels,
            datasets: chartData.datasets
          }}
          options={{
            maintainAspectRatio: false,
            elements: {
              line: {
                tension: 0.25
              }
            },
            interaction: {
              mode: "nearest",
              intersect: false
            },
            scales: {
              r: {
                beginAtZero: true,
                max: 100,
                ticks: { display: false },
                grid: {
                  circular: true,
                  color: colors.border
                },
                angleLines: {
                  color: colors.border
                },
                pointLabels: {
                  color: colors.textPrimary,
                  font: {
                    size: 13,
                    weight: "bold" as const
                  }
                }
              }
            },
            plugins: {
              legend: {
                position: "bottom"
              },
              tooltip: {
                enabled: true,
                backgroundColor: colors.textPrimary,
                titleColor: "#ffffff",
                bodyColor: colors.border,
                borderColor: colors.border,
                borderWidth: 1,
                padding: 12,
                displayColors: true,
                caretSize: 6,
                titleFont: {
                  size: 13,
                  weight: "bold" as const
                },
                bodyFont: {
                  size: 12
                },
                bodySpacing: 4,
                filter: (context: any) => {
                  if (isDemoMode) {
                    return context?.dataset?.isPrimary === true;
                  }
                  return true;
                },
                callbacks: {
                  title: (items: any) => {
                    return items[0]?.label || "";
                  },
                  label: (context: any) => {
                    const dataset = context.dataset || {};
                    const rawScore = dataset.rawScores?.[context.dataIndex] ?? 0;
                    return `${dataset.label}: ${rawScore}% category share`;
                  },
                  beforeBody: (items: any) => {
                    const label = items[0]?.label?.toLowerCase() || "";
                    const definition = AXIS_DEFINITIONS[label] || "";
                    const words = definition.split(" ");
                    const lines: string[] = [];
                    let currentLine = "";

                    words.forEach((word) => {
                      if ((currentLine + " " + word).trim().length > 42) {
                        lines.push(currentLine.trim());
                        currentLine = word;
                      } else {
                        currentLine += ` ${word}`;
                      }
                    });

                    if (currentLine.trim()) lines.push(currentLine.trim());

                    return lines;
                  }
                }
              }
            }
          }}
        />
      </div>

      <div
        style={{
          marginTop: 14,
          padding: "12px 14px",
          background: colors.background,
          border: `1px solid ${colors.border}`,
          borderRadius: 10
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: colors.textSecondary,
            marginBottom: 4,
            letterSpacing: "0.04em"
          }}
        >
          🧠 RADAR INSIGHT
        </div>

        <div
          style={{
            fontSize: 13,
            color: "#111827",
            lineHeight: 1.4
          }}
        >
          {getRadarInsight()}
        </div>
      </div>
    </ModuleContainer>
  );
}
